import { subFloat, year, month, day } from '@/utils/myUtils'

export const LOGISTICS_COMPANIES = [
  '顺丰速运',
  '圆通速递',
  '中通快递',
  '韵达快递',
  '申通快递',
  '德邦物流',
  '京东物流'
]

/** status_cooperative_doc：0草稿 1新建 2已占用 3待审核 4已审核 */
export const COOPERATIVE_DOC_STATUS = {
  DRAFT: 0,
  NEW: 1,
  LOCKED: 2,
  PENDING_AUDIT: 3,
  APPROVED: 4
}

/** 产品委外出货关联委外订单 */
export const COOPERATIVE_DOC_RELATION_TYPE = 2

export function createDefaultForm() {
  return {
    outsourcingtowarehouseId: null,
    globalId: null,
    outsourcingtowarehouseNumber: '',
    baseOutsourcingDocId: null,
    baseOutsourcingDocNumber: '',
    baseOutsourcingSupplierId: null,
    baseOutsourcingSupplierName: '',
    baseExpectedArrivalDate: `${year()}-${month()}-${day()}`,
    baseLogisticsCompany: '',
    baseLogisticsNumber: '',
    baseLogisticsContact: '',
    globalSearchNumber: '',
    docName: '产品委外出货',
    remark: '',
    statusCheck: 0,
    statusDoc: 0,
    statusCooperativeDoc: COOPERATIVE_DOC_STATUS.DRAFT,
    statusInventoryLock: 0,
    statusOutwarehouse: 0
  }
}

/** 是否已有出货单 ID（非 null / 空 / 0） */
export function hasDocId(id) {
  return id !== null && id !== undefined && id !== '' && Number(id) !== 0
}

export function formatOrderLabel(item) {
  const number =
    item.baseOutsourcingDocNumber || item.outsourcingDocNumber || ''
  const name = item.baseMaterialName || ''
  return name ? `${number} | ${name}` : number
}

export function formatProductLabel(row) {
  return row.baseMaterialName || row.baseMaterialNumber || '--'
}

export function formatSpecLabel(row) {
  if (row.specText) {
    return row.specText
  }
  if (Array.isArray(row.specifications) && row.specifications.length) {
    return row.specifications
      .map((item) => {
        if (typeof item === 'string') {
          return item
        }
        return item.specValue || item.specKey || ''
      })
      .filter(Boolean)
      .join(' ')
  }
  return row.baseSpecgroupDesc || '--'
}

export function formatQty(value) {
  if (value === undefined || value === null || value === '') {
    return '--'
  }
  return value
}

export function getCooperativeStatusLabel(status) {
  const map = {
    [COOPERATIVE_DOC_STATUS.DRAFT]: '草稿 · 待提交',
    [COOPERATIVE_DOC_STATUS.NEW]: '新建',
    [COOPERATIVE_DOC_STATUS.LOCKED]: '已占用库存',
    [COOPERATIVE_DOC_STATUS.PENDING_AUDIT]: '待审核',
    [COOPERATIVE_DOC_STATUS.APPROVED]: '已审核 · 委外商已通过'
  }
  return map[Number(status)] || '草稿 · 待提交'
}

function formatDateTime(dateStr) {
  if (!dateStr) {
    return `${year()}-${month()}-${day()} 00:00:00`
  }
  if (String(dateStr).length <= 10) {
    return `${dateStr} 00:00:00`
  }
  return dateStr
}

function resolveSpecgroupDesc(row) {
  if (row.baseSpecgroupDesc) {
    return row.baseSpecgroupDesc
  }
  if (row.baseMaterialSpecDesc) {
    return row.baseMaterialSpecDesc
  }
  if (Array.isArray(row.specifications) && row.specifications.length) {
    try {
      return JSON.stringify(row.specifications)
    } catch (error) {
      return formatSpecLabel(row)
    }
  }
  return ''
}

export function normalizeDetailRow(item, index = 0) {
  const row = { ...item }
  row._rowKey = row._rowKey || `detail-${Date.now()}-${index}`
  row.baseMaterialId = row.baseMaterialId || row.materialId
  row.baseSpecgroupId = row.baseSpecgroupId || row.baseMaterialSpecgroupId

  try {
    if (!row.specifications && row.baseMaterialSpecDesc) {
      row.specifications = JSON.parse(row.baseMaterialSpecDesc)
    } else if (!row.specifications && row.baseSpecgroupDesc) {
      row.specifications = JSON.parse(row.baseSpecgroupDesc)
    }
  } catch (error) {
    row.specifications = row.specifications || []
  }

  const shippedQty =
    row.valueInwarehouseQuantity || row.valueLockInwarehouseQuantity || 0
  const needRecalculateCanShip =
    row.valueUnfinishedQuantity === undefined ||
    row.valueUnfinishedQuantity === null ||
    (!row.baseOutsourcingQuantity && Number(row.valueUnfinishedQuantity) === 0)
  if (needRecalculateCanShip) {
    const canShipQty =
      row.valueCanDeliveryQuantity ??
      row.valueCanShipQuantity ??
      row.valueUnfinishedQuantity
    if (canShipQty !== undefined && canShipQty !== null) {
      row.valueUnfinishedQuantity = canShipQty
    } else {
      row.valueUnfinishedQuantity = subFloat(
        row.baseOutsourcingQuantity || 0,
        shippedQty
      )
    }
  }

  if (
    row.baseOutsourcingFinishedQuantity === undefined ||
    row.baseOutsourcingFinishedQuantity === null
  ) {
    row.baseOutsourcingFinishedQuantity = row.valueUnfinishedQuantity || 0
  }

  row.batchNumber = row.batchNumber || row.valueBatchNumber || ''

  initQualifiedLossQty(row)
  syncFinishedQtyFromParts(row)

  return row
}

function hasQtyValue(value) {
  return value !== undefined && value !== null && value !== ''
}

/**
 * 历史单缺字段兜底：
 * - 两字段都缺：良品=本次出货、损耗=0
 * - 只缺其一：用「本次出货 - 已有字段」补另一侧，避免 sync 覆盖本次出货
 */
function initQualifiedLossQty(row) {
  const hasQualified = hasQtyValue(row.valueQualifiedQuantity)
  const hasUnqualified = hasQtyValue(row.valueUnqualifiedQuantity)
  const finished = Number(row.baseOutsourcingFinishedQuantity) || 0

  if (!hasQualified && !hasUnqualified) {
    row.valueQualifiedQuantity = finished
    row.valueUnqualifiedQuantity = 0
    return
  }
  if (hasQualified && hasUnqualified) {
    row.valueQualifiedQuantity = Number(row.valueQualifiedQuantity) || 0
    row.valueUnqualifiedQuantity = Number(row.valueUnqualifiedQuantity) || 0
    return
  }
  if (hasQualified) {
    row.valueQualifiedQuantity = Number(row.valueQualifiedQuantity) || 0
    const loss = Number(subFloat(finished, row.valueQualifiedQuantity))
    row.valueUnqualifiedQuantity = loss < 0 ? 0 : loss
    return
  }
  row.valueUnqualifiedQuantity = Number(row.valueUnqualifiedQuantity) || 0
  const good = Number(subFloat(finished, row.valueUnqualifiedQuantity))
  row.valueQualifiedQuantity = good < 0 ? 0 : good
}

/** 良品 + 损耗 → 本次出货 */
export function syncFinishedQtyFromParts(row) {
  if (!row) {
    return row
  }
  const good = Number(row.valueQualifiedQuantity)
  const loss = Number(row.valueUnqualifiedQuantity)
  row.valueQualifiedQuantity =
    Number.isFinite(good) && good >= 0 ? good : 0
  row.valueUnqualifiedQuantity =
    Number.isFinite(loss) && loss >= 0 ? loss : 0
  // subFloat(a, -b) ≈ a + b，与项目浮点工具一致
  row.baseOutsourcingFinishedQuantity = subFloat(
    row.valueQualifiedQuantity,
    -row.valueUnqualifiedQuantity
  )
  return row
}

/** 从订单列表中查找委外订单行 */
export function findOrderRow(orderOptions, docId) {
  if (!docId || !Array.isArray(orderOptions)) {
    return null
  }
  return (
    orderOptions.find(
      (item) => (item.outsourcingDocId || item.baseOutsourcingDocId) === docId
    ) || null
  )
}

/**
 * 用委外订单数量信息补全可发数量（编辑已保存出货单时详情接口不含订单数量）
 */
export function mergeOrderIntoDetailRow(detailRow, orderRow) {
  if (!orderRow) {
    return detailRow
  }
  const row = { ...detailRow }
  const orderQty = Number(orderRow.baseOutsourcingQuantity) || 0
  const shippedQty =
    Number(
      orderRow.valueInwarehouseQuantity ?? orderRow.valueLockInwarehouseQuantity
    ) || 0
  const currentFinished = Number(row.baseOutsourcingFinishedQuantity) || 0

  row.baseOutsourcingQuantity = orderQty
  row.valueInwarehouseQuantity = shippedQty
  if (orderRow.valueCanDeliveryQuantity != null) {
    row.valueCanDeliveryQuantity = orderRow.valueCanDeliveryQuantity
  }
  if (orderRow.valueCanShipQuantity != null) {
    row.valueCanShipQuantity = orderRow.valueCanShipQuantity
  }

  let canShip
  if (
    orderRow.valueCanDeliveryQuantity != null &&
    orderRow.valueCanDeliveryQuantity !== ''
  ) {
    canShip = Number(orderRow.valueCanDeliveryQuantity)
  } else if (
    orderRow.valueCanShipQuantity != null &&
    orderRow.valueCanShipQuantity !== ''
  ) {
    canShip = Number(orderRow.valueCanShipQuantity)
  } else {
    canShip = subFloat(orderQty, shippedQty)
  }

  // 编辑已有出货单：若已出货数量已含本单，加回当前出货数作为可发上限
  if (hasDocId(row.outsourcingtowarehouseId) && currentFinished > 0) {
    const remainWithoutCurrent = subFloat(orderQty, shippedQty)
    if (remainWithoutCurrent < currentFinished) {
      canShip = subFloat(remainWithoutCurrent, 0) + currentFinished
    }
  }

  row.valueUnfinishedQuantity = canShip
  return row
}

export function applyHeaderFields(form, source = {}) {
  return {
    ...form,
    outsourcingtowarehouseId:
      source.outsourcingtowarehouseId || form.outsourcingtowarehouseId,
    globalId:
      source.outsourcingtowarehouseId || form.outsourcingtowarehouseId,
    outsourcingtowarehouseNumber:
      source.outsourcingtowarehouseNumber || form.outsourcingtowarehouseNumber,
    baseOutsourcingDocId:
      source.baseOutsourcingDocId ||
      source.outsourcingDocId ||
      form.baseOutsourcingDocId,
    baseOutsourcingDocNumber:
      source.baseOutsourcingDocNumber ||
      source.outsourcingDocNumber ||
      form.baseOutsourcingDocNumber,
    baseOutsourcingSupplierId:
      source.baseOutsourcingSupplierId || form.baseOutsourcingSupplierId,
    baseOutsourcingSupplierName:
      source.baseOutsourcingSupplierName || form.baseOutsourcingSupplierName,
    baseExpectedArrivalDate:
      source.baseExpectedArrivalDate ||
      source.valueExpectedArrivalDate ||
      form.baseExpectedArrivalDate,
    baseLogisticsCompany:
      source.baseLogisticsCompany || form.baseLogisticsCompany,
    baseLogisticsNumber:
      source.baseLogisticsNumber || form.baseLogisticsNumber,
    baseLogisticsContact:
      source.baseLogisticsContact ||
      source.baseContactPhone ||
      source.baseContactInfo ||
      form.baseLogisticsContact,
    globalSearchNumber:
      source.globalSearchNumber || form.globalSearchNumber,
    docName: source.docName || form.docName,
    remark: source.remark !== undefined ? source.remark : form.remark,
    statusCheck:
      source.statusCheck !== undefined ? source.statusCheck : form.statusCheck,
    statusDoc:
      source.statusDoc !== undefined ? source.statusDoc : form.statusDoc,
    statusCooperativeDoc:
      source.statusCooperativeDoc !== undefined
        ? source.statusCooperativeDoc
        : source.status_cooperative_doc !== undefined
          ? source.status_cooperative_doc
          : form.statusCooperativeDoc,
    statusInventoryLock:
      source.statusInventoryLock !== undefined
        ? source.statusInventoryLock
        : source.status_inventory_lock !== undefined
          ? source.status_inventory_lock
          : form.statusInventoryLock,
    statusOutwarehouse:
      source.statusOutwarehouse !== undefined
        ? source.statusOutwarehouse
        : source.status_outwarehouse !== undefined
          ? source.status_outwarehouse
          : form.statusOutwarehouse
  }
}

/** 从委外订单行提取表单所需字段 */
export function pickOrderFormFields(orderRow) {
  if (!orderRow) {
    return {}
  }
  return {
    baseOutsourcingDocId:
      orderRow.baseOutsourcingDocId || orderRow.outsourcingDocId || null,
    baseOutsourcingDocNumber:
      orderRow.baseOutsourcingDocNumber ||
      orderRow.outsourcingDocNumber ||
      '',
    baseOutsourcingSupplierId: orderRow.baseOutsourcingSupplierId,
    baseOutsourcingSupplierName: orderRow.baseOutsourcingSupplierName,
    globalSearchNumber: orderRow.globalSearchNumber
  }
}

/** 从委外订单行提取出货明细所需字段（对齐 t_srm_coop_product_delivery 物料列） */
export function pickOrderDetailSource(orderRow) {
  if (!orderRow) {
    return {}
  }
  return {
    baseOutsourcingDocId:
      orderRow.baseOutsourcingDocId || orderRow.outsourcingDocId,
    baseOutsourcingDocNumber:
      orderRow.baseOutsourcingDocNumber || orderRow.outsourcingDocNumber,
    baseOutsourcingSupplierId: orderRow.baseOutsourcingSupplierId,
    baseOutsourcingSupplierName: orderRow.baseOutsourcingSupplierName,
    baseMaterialId: orderRow.baseMaterialId,
    baseMaterialNumber: orderRow.baseMaterialNumber,
    baseMaterialName: orderRow.baseMaterialName,
    baseMaterialUnitId: orderRow.baseMaterialUnitId,
    baseMaterialUnitName: orderRow.baseMaterialUnitName,
    baseCustomerMaterialNumber: orderRow.baseCustomerMaterialNumber,
    baseSpecgroupId:
      orderRow.baseSpecgroupId || orderRow.baseMaterialSpecgroupId,
    baseSpecgroupDesc: orderRow.baseSpecgroupDesc,
    baseMaterialSpecDesc: orderRow.baseMaterialSpecDesc,
    specifications: orderRow.specifications,
    globalSearchNumber: orderRow.globalSearchNumber,
    baseOutsourcingQuantity: orderRow.baseOutsourcingQuantity,
    valueInwarehouseQuantity: orderRow.valueInwarehouseQuantity,
    valueLockInwarehouseQuantity: orderRow.valueLockInwarehouseQuantity,
    valueCanDeliveryQuantity: orderRow.valueCanDeliveryQuantity,
    valueCanShipQuantity: orderRow.valueCanShipQuantity,
    valueUnfinishedQuantity: orderRow.valueUnfinishedQuantity,
    batchNumber: orderRow.batchNumber || orderRow.valueBatchNumber,
    remark: orderRow.remark
  }
}

export function validateDeliveryForm({
  form,
  detailList,
  requireOrder = true,
  requireQty = true,
  allowEmptyDetails = false
}) {
  if (requireOrder && !form.baseOutsourcingDocId) {
    return { valid: false, message: '请选择委外订单号' }
  }
  if (!detailList.length) {
    if (allowEmptyDetails) {
      return { valid: true }
    }
    return { valid: false, message: '请至少添加一条出货明细' }
  }
  for (const row of detailList) {
    // 提交前强制同步，避免 input 未 blur 导致本次出货仍是旧值
    syncFinishedQtyFromParts(row)
    const good = parseFloat(row.valueQualifiedQuantity)
    const loss = parseFloat(row.valueUnqualifiedQuantity)
    if (Number.isNaN(good) || good < 0) {
      return { valid: false, message: '良品数量不能小于0' }
    }
    if (Number.isNaN(loss) || loss < 0) {
      return { valid: false, message: '损耗数量不能小于0' }
    }
    const qty = parseFloat(row.baseOutsourcingFinishedQuantity)
    if (requireQty && (!qty || qty <= 0)) {
      return { valid: false, message: '本次出货数量必须大于0' }
    }
    const maxQty = parseFloat(row.valueUnfinishedQuantity)
    if (
      requireQty &&
      !Number.isNaN(maxQty) &&
      maxQty >= 0 &&
      qty > maxQty
    ) {
      return {
        valid: false,
        message: `${formatProductLabel(row)} 的出货数量不能超过可发数量`
      }
    }
  }
  return { valid: true }
}

/**
 * 构建 outsourcingtowarehouseDto（对齐接口入参示例）
 */
export function buildOutsourcingtowarehouseDto({
  form,
  detailList,
  docName = ''
}) {
  const row = detailList[0] || {}
  if (detailList[0]) {
    syncFinishedQtyFromParts(row)
  }
  const finishedQty = Number(row.baseOutsourcingFinishedQuantity) || 0
  const dto = {
    outsourcingtowarehouseId: form.outsourcingtowarehouseId,
    baseOutsourcingDocId: form.baseOutsourcingDocId,
    baseOutsourcingDocNumber: form.baseOutsourcingDocNumber,
    baseMaterialId: row.baseMaterialId,
    baseMaterialNumber: row.baseMaterialNumber,
    baseMaterialName: row.baseMaterialName,
    baseMaterialUnitId: row.baseMaterialUnitId,
    baseMaterialUnitName: row.baseMaterialUnitName,
    baseOutsourcingFinishedQuantity: finishedQty,
    baseOutsourcingInwarehouseQuantity:
      row.baseOutsourcingInwarehouseQuantity ?? finishedQty,
    baseLogisticsCompany: form.baseLogisticsCompany,
    baseLogisticsNumber: form.baseLogisticsNumber,
    baseLogisticsContact: form.baseLogisticsContact,
    baseExpectedArrivalDate: formatDateTime(form.baseExpectedArrivalDate),
    statusCooperativeDoc: form.statusCooperativeDoc,
    statusInventoryLock: form.statusInventoryLock,
    batchNumber: row.batchNumber || '',
    remark: form.remark || row.remark || ''
  }

  if (form.outsourcingtowarehouseNumber) {
    dto.outsourcingtowarehouseNumber = form.outsourcingtowarehouseNumber
  }
  if (row.baseSpecgroupId) {
    dto.baseSpecgroupId = row.baseSpecgroupId
  }
  if (resolveSpecgroupDesc(row)) {
    dto.baseSpecgroupDesc = resolveSpecgroupDesc(row)
  }
  dto.valueQualifiedQuantity = Number(row.valueQualifiedQuantity) || 0
  dto.valueUnqualifiedQuantity = Number(row.valueUnqualifiedQuantity) || 0
  if (row.valueUnitPrice != null) {
    dto.valueUnitPrice = row.valueUnitPrice
  }
  if (row.valueTotlePrice != null) {
    dto.valueTotlePrice = row.valueTotlePrice
  }
  if (docName) {
    dto.docName = docName
  }

  if (!hasDocId(dto.outsourcingtowarehouseId)) {
    delete dto.outsourcingtowarehouseId
  }

  return dto
}

export function resolveSysDocRelation(source = {}) {
  if (source.sysDocRelationDto?.relationDocFromId) {
    return {
      relationDocFromId: String(source.sysDocRelationDto.relationDocFromId),
      relationType:
        source.sysDocRelationDto.relationType ?? COOPERATIVE_DOC_RELATION_TYPE
    }
  }
  const docId = source.baseOutsourcingDocId || source.outsourcingDocId
  if (docId) {
    return {
      relationDocFromId: String(docId),
      relationType: COOPERATIVE_DOC_RELATION_TYPE
    }
  }
  return null
}

/** 构建完整接口入参：outsourcingtowarehouseDto + sysDocRelationDto */
export function buildRequestPayload({
  form,
  detailList,
  sysDocRelationDto = null,
  docName = ''
}) {
  const outsourcingtowarehouseDto = buildOutsourcingtowarehouseDto({
    form,
    detailList,
    docName
  })
  const payload = { outsourcingtowarehouseDto }
  const relation =
    sysDocRelationDto?.relationDocFromId != null
      ? {
        relationDocFromId: String(sysDocRelationDto.relationDocFromId),
        relationType:
            sysDocRelationDto.relationType ?? COOPERATIVE_DOC_RELATION_TYPE
      }
      : resolveSysDocRelation(form)
  if (relation) {
    payload.sysDocRelationDto = relation
  }
  return payload
}

/** saveDraft / submitDraft / 出库 / 撤销出库 仅传出货单 ID */
export function buildDraftActionPayload(outsourcingtowarehouseId) {
  return { outsourcingtowarehouseId }
}

export function buildOutWarehouseActionPayload(outsourcingtowarehouseId) {
  return buildDraftActionPayload(outsourcingtowarehouseId)
}

/** 审核/取消审核入参 */
export function buildAuditPayload(globalId, type) {
  return { globalId, type }
}

export function resolveAuditGlobalId(form) {
  return form.globalId || form.outsourcingtowarehouseId
}

export function buildSubmitPayload(options) {
  return buildRequestPayload(options)
}

export function buildAddPayload(options) {
  const payload = buildRequestPayload(options)
  payload.outsourcingtowarehouseDto.statusCooperativeDoc =
    COOPERATIVE_DOC_STATUS.DRAFT
  payload.outsourcingtowarehouseDto.statusInventoryLock = 0
  if (!hasDocId(options.form.outsourcingtowarehouseId)) {
    delete payload.outsourcingtowarehouseDto.outsourcingtowarehouseId
  }
  return payload
}

/** 出货列表页统计卡片（getDeliveryStat） */
export function normalizeDeliveryStat(source = {}) {
  return [
    {
      key: 'total',
      title: '出货单总数',
      desc: source.totalDesc || '当前供应商全部有效出货单',
      count: Number(source.totalCount ?? 0),
      color: '#303133'
    },
    {
      key: 'pendingAudit',
      title: '待审核',
      desc: source.pendingAuditDesc || '等待委外商审核的出货单',
      count: Number(source.pendingAuditCount ?? 0),
      color: '#e6a23c'
    },
    {
      key: 'approved',
      title: '已审核',
      desc: source.approvedDesc || '审核通过的出货单',
      count: Number(source.approvedCount ?? 0),
      color: '#409eff'
    },
    {
      key: 'outWarehouse',
      title: '已出库',
      desc: source.outWarehouseDesc || '已完成出库操作的出货单',
      count: Number(source.outWarehouseCount ?? 0),
      color: '#67c23a'
    }
  ]
}
